// CRM forms and booking calendars on a landing page. Both are the CRM's own
// public pages (/f/<form>, /book/<calendar>) in an iframe on the page's own host
// (relative, so a custom domain keeps the visitor on that domain). A lead lands in
// the CRM with the page's attribution, the form keeps its own screens (questions,
// not a fit, booking a call) and nothing about the form is rebuilt here.
//
// Server-rendered pages: the server HTML and the browser's hydration render show
// a sized empty box (the frame address needs the visit's referrer and ad click
// ids, which only the browser knows); the frame mounts inside that same box right
// after hydration, so hydration never sees a mismatch.
//
// The frames talk back with window messages:
//   { type: 'nla-height', height }                       the page's content height
//   { type: 'nla-form-done', qualified, redirect, calendar }   a form was sent
//   { type: 'nla-booked' }                               a call was booked
// Each is taken only from the frame's own window (e.source), and the funnel ones
// only from the page's own origin.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type RefObject } from 'react';
import { ATTR_KEYS, browserCookies, browserStorage, firstTouchQuery, pickAttribution, rememberedJourney, type VisitJourney } from '../../crmAttribution';
import { bookingEmbedUrl } from '../model/pages';
import { FORM_FIELD_DESIGN_MESSAGE, FORM_FIELD_DESIGN_QUERY, formFieldDesignQuery, readFormFieldDesignQuery, type FormFieldDesign } from '../model/form-fields';
import { SsrContext } from './ssr-context';

/**
 * A page preview's own capability: the ?pt= token of the page being previewed
 * (PublicLandingPage provides it on token previews only). The form frames on that
 * page pass it on with the page id, so the form opens in preview for anyone holding
 * the preview link, signed in or not: a draft form loads and nothing is saved.
 */
export const FormPreviewTokenContext = createContext<string | null>(null);

/** A CRM form was sent (PublicForm's nla-form-done message). */
export interface FormDone {
  /** False: the form shows its "not a fit" screen. */
  qualified: boolean;
  /** True: the form is sending the whole window to its redirect address. */
  redirect: boolean;
  /** True: the form shows its booking calendar; nla-booked follows a booking. */
  calendar: boolean;
}

/** The form box before the form reports its height (a contact screen). */
export const FORM_FRAME_HEIGHT = 520;
/** The booking calendar never gets shorter than this. */
export const BOOKING_MIN_HEIGHT = 640;
const MAX_FRAME_HEIGHT = 6000;

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * The address of a CRM form framed on a page: the form opens straight on its
 * first question (start=1) in the page's button color (accent), and carries the
 * visit (page, landing, referrer and the UTM tags and ad click ids of the page's
 * own address) plus the page id (pg), so the lead counts for this page. A preview
 * with the page's preview token adds it (pt), so the form trusts the preview link
 * itself. Pure.
 */
export function crmFormSrc(slug: string, opts: {
  pageId?: string;
  accent?: string;
  /** Let a wide page layout use the full frame width for the form's questions. */
  expanded?: boolean;
  fieldDesign?: FormFieldDesign;
  /** Answers are checked but never saved as a lead. */
  preview?: boolean;
  /** The previewed page's own token (only with preview and pageId). */
  previewToken?: string | null;
  /** The page address without its query string. */
  pageUrl?: string;
  referrer?: string;
  /** The page's own query string ('?…'), for the attribution keys. */
  search?: string;
  /** This visit's attribution (pageVisit): an ad click remembered from an earlier page or day. Wins over search. */
  visit?: VisitJourney;
} = {}): string {
  const q = new URLSearchParams();
  q.set('embed', '1');
  q.set('start', '1');
  if (opts.visit?.tracking_consent === false) q.set('nla_consent', '0');
  if (opts.expanded) q.set('layout', 'expanded');
  const fieldDesign = formFieldDesignQuery(opts.fieldDesign);
  if (fieldDesign) q.set(FORM_FIELD_DESIGN_QUERY, fieldDesign);
  if (opts.pageId) q.set('pg', opts.pageId);
  if (opts.accent && HEX.test(opts.accent)) q.set('accent', opts.accent);
  if (opts.pageUrl) {
    q.set('page', opts.pageUrl.slice(0, 500));
    q.set('landing', (opts.visit?.landing || opts.pageUrl).slice(0, 500));
  }
  const referrer = opts.visit ? opts.visit.referrer : opts.referrer;
  if (referrer) q.set('referrer', referrer.slice(0, 500));
  const keys = opts.visit || pickAttribution(opts.search ?? '');
  for (const key of ATTR_KEYS) {
    const v = keys[key];
    if (v && opts.visit?.tracking_consent !== false) q.set(key, v.slice(0, key === 'fbc' || key === 'fbp' ? 500 : 200));
  }
  const first = opts.visit?.tracking_consent === false ? {} : firstTouchQuery(opts.visit?.first_touch);
  if (Object.keys(first).length) q.set('first_touch', JSON.stringify(first));
  if (opts.preview && opts.pageId && opts.previewToken) q.set('pt', opts.previewToken.slice(0, 64));
  if (opts.preview) q.set('preview', '1');
  return `/f/${encodeURIComponent(slug)}?${q.toString()}`;
}

/**
 * This visit's attribution on the page: the ad click in its address, or the one
 * remembered from an earlier page or day (saved here for 90 days, like the website snippet).
 */
export function pageVisit(): VisitJourney {
  const params = new URLSearchParams(window.location.search);
  if ((navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true || params.get('nla_consent') === '0') return { tracking_consent: false };
  if (window.parent !== window && params.get('nla_embed') === '1') {
    const visit = firstTouchQuery({ ...pickAttribution(window.location.search), landing: params.get('landing'), referrer: params.get('referrer') });
    const first = firstTouchQuery(params.get('first_touch'));
    return { ...visit, first_touch: Object.keys(first).length ? first : visit, last_touch: visit };
  }
  return rememberedJourney({
    search: window.location.search, here: pageAddress(), referrer: document.referrer || '', storage: browserStorage(),
    cookie: browserCookies(),
    documentId: performance.timeOrigin,
    gpc: (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true,
  });
}

/** A booking calendar's frame address with the visit, so a call booked there keeps its ad click. */
export function bookingSrc(slug: string, visit: VisitJourney = {}): string {
  const base = bookingEmbedUrl(slug, '');
  if (!base) return '';
  const q = new URLSearchParams();
  if (visit.tracking_consent === false) return `${base}&nla_consent=0`;
  for (const key of [...ATTR_KEYS, 'landing', 'referrer'] as const) {
    const v = visit[key];
    if (v) q.set(key, v.slice(0, key === 'landing' || key === 'referrer' || key === 'fbc' || key === 'fbp' ? 500 : 200));
  }
  const first = firstTouchQuery(visit.first_touch);
  if (Object.keys(first).length) q.set('first_touch', JSON.stringify(first));
  const extra = q.toString();
  return extra ? `${base}&${extra}` : base;
}

/** This page's address without the query string, or '' when the browser has none. */
function pageAddress(): string {
  try {
    const { origin, pathname } = window.location;
    return /^https?:\/\//.test(origin) ? `${origin}${pathname}` : '';
  } catch {
    return '';
  }
}

type FrameHandlers = { height?: (h: number) => void; done?: (d: FormDone) => void; booked?: () => void };

/**
 * Listens to one frame's messages. The listener sits on the window that holds the
 * frame (the editor canvas renders the page inside its own frame, so that is not
 * always this script's window).
 */
function useFrameMessages(frame: RefObject<HTMLIFrameElement | null>, active: boolean, on: FrameHandlers) {
  const handlers = useRef(on);
  handlers.current = on;
  useEffect(() => {
    const el = frame.current;
    if (!active || !el) return;
    const win = el.ownerDocument.defaultView;
    if (!win) return;
    const onMessage = (e: MessageEvent) => {
      if (!e.source || e.source !== el.contentWindow) return;
      const d = e.data as { type?: unknown; height?: unknown; qualified?: unknown; redirect?: unknown; calendar?: unknown } | null;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'nla-height') {
        const h = Number(d.height);
        if (Number.isFinite(h) && h > 0) handlers.current.height?.(Math.min(MAX_FRAME_HEIGHT, Math.ceil(h)));
        return;
      }
      if (e.origin !== window.location.origin) return;
      if (d.type === 'nla-form-done') {
        handlers.current.done?.({ qualified: d.qualified === true, redirect: d.redirect === true, calendar: d.calendar === true });
      } else if (d.type === 'nla-booked') {
        handlers.current.booked?.();
      }
    };
    win.addEventListener('message', onMessage);
    return () => win.removeEventListener('message', onMessage);
  }, [frame, active]);
}

/**
 * A CRM form on a page: the form itself in a frame that grows to fit it.
 * `blocked` lays a clear layer over it (the editor's Edit mode, thumbnails), so a
 * click selects the section instead of answering the form.
 */
export default function CrmFormFrame({ slug, name, pageId, accent, expanded = false, fieldDesign, preview, blocked, onDone, onBooked }: {
  slug: string;
  name?: string;
  pageId?: string;
  /** The page's form accent (formAccentFor): the form's buttons take it. */
  accent?: string;
  /** Opt in to a wider embedded form without changing existing form layouts. */
  expanded?: boolean;
  fieldDesign?: FormFieldDesign;
  preview?: boolean;
  blocked?: boolean;
  onDone?: (done: FormDone) => void;
  onBooked?: () => void;
}) {
  const ssr = useContext(SsrContext);
  const previewToken = useContext(FormPreviewTokenContext);
  const ref = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const designQuery = formFieldDesignQuery(fieldDesign);
  const designRef = useRef(designQuery);
  designRef.current = designQuery;
  const sendDesign = useCallback(() => {
    ref.current?.contentWindow?.postMessage({
      type: FORM_FIELD_DESIGN_MESSAGE,
      design: readFormFieldDesignQuery(designRef.current ?? null) ?? null,
    }, window.location.origin);
  }, []);
  useEffect(() => {
    if (ssr) return;
    setSrc(crmFormSrc(slug, {
      pageId, accent, expanded, fieldDesign: readFormFieldDesignQuery(designRef.current ?? null), preview, previewToken,
      pageUrl: pageAddress(),
      visit: preview ? undefined : pageVisit(),
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      search: window.location.search,
    }));
  }, [ssr, slug, pageId, accent, expanded, preview, previewToken]);
  // While the visitor answers, the box only grows (and eases into it), so the
  // page under it does not jump up and down from one question to the next. Once
  // the form is sent it fits its end screen again.
  const sent = useRef(false);
  const last = useRef(0);
  const width = useRef(0);
  const fitAfterResize = useRef(false);
  const measureWidth = useCallback(() => {
    // clientWidth is the layout width, unaffected by the editor canvas scale.
    const next = boxRef.current?.clientWidth ?? 0;
    if (next <= 0 || next === width.current) return;
    if (width.current > 0) {
      fitAfterResize.current = true;
      // Release the old question-height floor. Keeping the last measurement
      // also handles a width change that does not change the current question's
      // height, since the child suppresses identical height messages.
      setHeight(last.current || null);
    }
    width.current = next;
  }, []);
  useEffect(() => {
    const box = boxRef.current;
    if (ssr || !box) return;
    measureWidth();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measureWidth);
    observer.observe(box);
    return () => observer.disconnect();
  }, [ssr, measureWidth]);
  // A different form address reloads the frame. Its previous height and submit
  // state no longer apply, including when a wider layout needs less height.
  useEffect(() => {
    sent.current = false;
    last.current = 0;
    fitAfterResize.current = false;
    setHeight(null);
  }, [src]);
  // Changing presentation must not navigate away from a visitor's current
  // question. The URL seeds the first paint; later edits update only styles.
  useEffect(() => {
    if (ssr || !src) return;
    fitAfterResize.current = true;
    if (last.current) setHeight(last.current);
    sendDesign();
  }, [ssr, src, designQuery, sendDesign]);
  useFrameMessages(ref, !!src, {
    height: (h) => {
      // A child's message can arrive before the parent's resize observer.
      measureWidth();
      const fit = fitAfterResize.current;
      fitAfterResize.current = false;
      last.current = Math.max(120, h);
      setHeight((prev) => (sent.current || fit || prev == null ? last.current : Math.max(prev, last.current)));
    },
    // The end screen's height can arrive before this message: fit to the latest one.
    done: (d) => { sent.current = true; if (last.current) setHeight(last.current); onDone?.(d); },
    booked: onBooked,
  });
  return (
    <div ref={boxRef} data-crm-form={slug} className="relative w-full motion-safe:transition-[height] motion-safe:duration-200 motion-safe:ease-out"
      style={{ height: height ?? FORM_FRAME_HEIGHT }}>
      {src && <iframe ref={ref} src={src} onLoad={sendDesign} title={name || 'Form'} className="block h-full w-full border-0" />}
      {blocked && <div aria-hidden data-frame-shield className="absolute inset-0" />}
    </div>
  );
}

/** A CRM booking calendar on a page (/book/<calendar>?embed=1), at least 640px tall. */
export function BookingFrame({ slug, title = 'Book a meeting', onBooked }: {
  slug: string;
  title?: string;
  onBooked?: () => void;
}) {
  const ssr = useContext(SsrContext);
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  // Built after hydration, like the form's: the visit is only known in the browser.
  const [src, setSrc] = useState('');
  useEffect(() => { if (!ssr) setSrc(bookingSrc(slug, pageVisit())); }, [ssr, slug]);
  const live = !ssr && !!src;
  useFrameMessages(ref, live, { height: (h) => setHeight(Math.max(BOOKING_MIN_HEIGHT, h)), booked: onBooked });
  return (
    <div data-crm-booking={slug} className="relative w-full" style={{ height: height ?? BOOKING_MIN_HEIGHT }}>
      {live && <iframe ref={ref} src={src} title={title} className="block h-full w-full border-0" style={{ borderRadius: 12 }} />}
    </div>
  );
}
